import { ArrowBack } from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PartnerDocumentsPanel from "./PartnerDocumentsPanel";
import PartnerProfilePanel from "./PartnerProfilePanel";
import CommissionLedgerPanel from "./CommissionLedgerPanel";
import PayoutHistoryPanel from "./PayoutHistoryPanel";
import PartnerMessagesPanel from "./PartnerMessagesPanel";
import PartnerSupportPanel from "./PartnerSupportPanel";
import PartnerClientsPanel from "./PartnerClientsPanel";
import {
  getPartner,
  partnerStatusTone,
  reactivatePartner,
  suspendPartner,
} from "../referralPartnersApi";
import QueueCountBadge from "../QueueCountBadge";
import { usePartnerOverview } from "../referralOverviewApi";

const TABS = [
  { id: "profile", label: "Profile" },
  {
    id: "clients",
    label: "Clients",
    permission: P.REFERRAL.LEAD_READ,
    countKey: "referredClients",
  },
  { id: "documents", label: "Documents", countKey: "pendingDocuments" },
  { id: "commission", label: "Commission", countKey: "unpaidCommissions" },
  {
    id: "messages",
    label: "Alerts/Messages",
    permission: P.REFERRAL.MESSAGE_READ,
    countKey: "unreadMessages",
  },
  {
    id: "support",
    label: "Support tickets",
    permission: P.REFERRAL.MESSAGE_READ,
    countKey: "openSupportTickets",
  },
];

// Older links and notifications still say "ledger".
const TAB_ALIASES = { ledger: "commission" };

/** A tab names its queue and wears its pending number as a badge; zero hides it. */
const tabLabel = (label, count) => (
  <div className="flex items-center gap-1">
    {label} <QueueCountBadge count={count} />
  </div>
);

const PartnerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canUpdate = usePermission(P.REFERRAL.PARTNER_UPDATE);
  const canSuspend = usePermission(P.REFERRAL.PARTNER_SUSPEND);
  const canReadMessages = usePermission(P.REFERRAL.MESSAGE_READ);
  const canReadLeads = usePermission(P.REFERRAL.LEAD_READ);
  const canPay = usePermission(P.REFERRAL.PAYOUT_CREATE);
  // A tab nobody can open is worse than a missing one: it looks like the data is empty.
  const visibleTabs = TABS.filter((entry) => {
    if (entry.permission === P.REFERRAL.MESSAGE_READ) return canReadMessages;
    if (entry.permission === P.REFERRAL.LEAD_READ) return canReadLeads;
    return true;
  });
  // One shared cache key for every tab badge: the counts arrive without opening any tab.
  const { data: counts } = usePartnerOverview(id);
  // The tab lives in the URL (?tab=commission) so a refresh, the back button or a shared link
  // lands on the same tab. A notification can still open one through navigation state.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") || location.state?.tab;
  const tab =
    visibleTabs.find((entry) => entry.id === (TAB_ALIASES[requestedTab] || requestedTab))?.id ||
    "profile";
  const setTab = (next) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("tab", next);
        // Filters belong to the tab they were set on.
        params.delete("status");
        return params;
      },
      { replace: true }
    );
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [reactivateAsk, setReactivateAsk] = useState(false);

  const {
    data: partner,
    isLoading,
    isError,
    refetch,
  } = useQuery(["referral-partner", id], () => getPartner(id), { refetchOnMount: true });

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(["referral-partners"]);
    queryClient.invalidateQueries(["referral-overview"]);
    queryClient.invalidateQueries(["referral-overview", "partner", id]);
  };

  const suspendMutation = useMutation((reason) => suspendPartner(id, reason), {
    onSuccess: () => {
      toast.success("Partner suspended");
      setSuspendOpen(false);
      setSuspendReason("");
      refresh();
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not suspend"
      ),
  });

  const reactivateMutation = useMutation(() => reactivatePartner(id), {
    onSuccess: () => {
      toast.success("Partner reactivated");
      setReactivateAsk(false);
      refresh();
    },
    onError: (err) =>
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not reactivate"
      ),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !partner) {
    return (
      <div className="px-4 mt-4">
        <Typography color="error">Could not load this referral partner.</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/admin/admin-referral")}
          sx={{ mt: 2 }}
        >
          Back to partners
        </Button>
      </div>
    );
  }

  const suspended = partner.status === "SUSPENDED";

  return (
    <div className="px-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Button startIcon={<ArrowBack />} onClick={() => navigate("/admin/admin-referral")}>
            Partners
          </Button>
          <Typography variant="h5" className="font-semibold">
            {partner.displayName}
          </Typography>
          <Chip label={partner.status} color={partnerStatusTone(partner.status)} size="small" />
        </div>
        {canSuspend ? (
          suspended ? (
            <Button variant="contained" color="success" onClick={() => setReactivateAsk(true)}>
              Reactivate
            </Button>
          ) : (
            <Button variant="outlined" color="error" onClick={() => setSuspendOpen(true)}>
              Suspend
            </Button>
          )
        ) : null}
      </div>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider", minHeight: 44 }}
      >
        {visibleTabs.map((entry) => (
          <Tab
            key={entry.id}
            value={entry.id}
            label={entry.countKey ? tabLabel(entry.label, counts?.[entry.countKey]) : entry.label}
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 44 }}
          />
        ))}
      </Tabs>

      {tab === "profile" ? (
        <PartnerProfilePanel
          partner={partner}
          canUpdate={canUpdate}
          canRevealBank={canPay}
          onChanged={refresh}
        />
      ) : tab === "clients" ? (
        <PartnerClientsPanel partnerId={partner.id} onChanged={refresh} />
      ) : tab === "documents" ? (
        <PartnerDocumentsPanel partner={partner} canVerify={canUpdate} onChanged={refresh} />
      ) : tab === "messages" ? (
        <PartnerMessagesPanel
          partnerId={partner.id}
          partnerName={partner.displayName}
          initialClientId={location.state?.clientId ?? null}
        />
      ) : tab === "support" ? (
        <PartnerSupportPanel partnerId={partner.id} />
      ) : (
        <div className="space-y-6">
          <CommissionLedgerPanel partnerId={partner.id} />
          <PayoutHistoryPanel partnerId={partner.id} onChanged={refresh} />
        </div>
      )}

      <Dialog open={suspendOpen} onClose={() => setSuspendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend {partner.displayName}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            They keep their attribution and ledger but cannot use the portal until reactivated. A
            reason is required.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Suspension reason"
            value={suspendReason}
            onChange={(event) => setSuspendReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!suspendReason.trim() || suspendMutation.isLoading}
            onClick={() => suspendMutation.mutate(suspendReason.trim())}
          >
            Suspend partner
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={reactivateAsk}
        title="Reactivate partner?"
        message={`${partner.displayName} will be able to use the portal again.`}
        confirmLabel="Reactivate"
        loading={reactivateMutation.isLoading}
        onConfirm={() => reactivateMutation.mutate()}
        onCancel={() => setReactivateAsk(false)}
      />
    </div>
  );
};

export default PartnerDetail;
